import * as R from 'ramda'
import type { AnyAction } from 'redux'
import { createSelector } from 'reselect'
import { type ApiError, Defaults, moment } from '@pbt/pbt-ui-components'

import type { InventoryItemTracking } from '~/types/entities/inventoryItemTracking'
import { secondLevelMerge } from '~/utils'
import { getErrorMessage } from '~/utils/errors'
import { getInventoryItemTrackingKey } from '~/utils/inventoryItemTrackingHelpers'

import type { RootState } from '../index'

export const FETCH_INVENTORY_ITEM_TRACKING_FOR_VARIATION =
  'inventoryItemTracking/FETCH_INVENTORY_ITEM_TRACKING_FOR_VARIATION'
export const FETCH_INVENTORY_ITEM_TRACKING_FOR_VARIATION_SUCCESS =
  'inventoryItemTracking/FETCH_INVENTORY_ITEM_TRACKING_FOR_VARIATION_SUCCESS'
export const FETCH_INVENTORY_ITEM_TRACKING_FOR_VARIATION_FAILURE =
  'inventoryItemTracking/FETCH_INVENTORY_ITEM_TRACKING_FOR_VARIATION_FAILURE'

export const FETCH_MORE_INVENTORY_ITEM_TRACKING_FOR_VARIATION =
  'inventoryItemTracking/FETCH_MORE_INVENTORY_ITEM_TRACKING_FOR_VARIATION'
export const FETCH_MORE_INVENTORY_ITEM_TRACKING_FOR_VARIATION_SUCCESS =
  'inventoryItemTracking/FETCH_MORE_INVENTORY_ITEM_TRACKING_FOR_VARIATION_SUCCESS'
export const FETCH_MORE_INVENTORY_ITEM_TRACKING_FOR_VARIATION_FAILURE =
  'inventoryItemTracking/FETCH_MORE_INVENTORY_ITEM_TRACKING_FOR_VARIATION_FAILURE'

export const FETCH_MORE_INVENTORY_ITEM_TRACKING_FOR_VACCINE =
  'inventoryItemTracking/FETCH_MORE_INVENTORY_ITEM_TRACKING_FOR_VACCINE'
export const FETCH_MORE_INVENTORY_ITEM_TRACKING_FOR_VACCINE_SUCCESS =
  'inventoryItemTracking/FETCH_MORE_INVENTORY_ITEM_TRACKING_FOR_VACCINE_SUCCESS'
export const FETCH_MORE_INVENTORY_ITEM_TRACKING_FOR_VACCINE_FAILURE =
  'inventoryItemTracking/FETCH_MORE_INVENTORY_ITEM_TRACKING_FOR_VACCINE_FAILURE'

export const FETCH_MORE_INVENTORY_ITEM_TRACKING_FOR_VACCINE_WITH_VARIATION =
  'inventoryItemTracking/FETCH_MORE_INVENTORY_ITEM_TRACKING_FOR_VACCINE_WITH_VARIATION'
export const FETCH_MORE_INVENTORY_ITEM_TRACKING_FOR_VACCINE_WITH_VARIATION_SUCCESS =
  'inventoryItemTracking/FETCH_MORE_INVENTORY_ITEM_TRACKING_FOR_VACCINE_WITH_VARIATION_SUCCESS'
export const FETCH_MORE_INVENTORY_ITEM_TRACKING_FOR_VACCINE_WITH_VARIATION_FAILURE =
  'inventoryItemTracking/FETCH_MORE_INVENTORY_ITEM_TRACKING_FOR_VACCINE_WITH_VARIATION_FAILURE'

export const UPDATE_INVENTORY_ITEM_TRACKING =
  'inventoryItemTracking/UPDATE_INVENTORY_ITEM_TRACKING'

export const CLEAR_INVENTORY_ITEM_TRACKING =
  'inventoryItemTracking/CLEAR_INVENTORY_ITEM_TRACKING'

export const fetchInventoryItemTrackingForVariation = (
  variationId: string,
  from: number,
  to: number,
) => ({
  type: FETCH_INVENTORY_ITEM_TRACKING_FOR_VARIATION,
  variationId,
  from,
  to,
})
export const fetchInventoryItemTrackingForVariationSuccess = (
  list: string[],
  from: number,
  totalCount: number,
) => ({
  type: FETCH_INVENTORY_ITEM_TRACKING_FOR_VARIATION_SUCCESS,
  list,
  from,
  totalCount,
})
export const fetchInventoryItemTrackingForVariationFailure = (
  error: ApiError,
) => ({
  type: FETCH_INVENTORY_ITEM_TRACKING_FOR_VARIATION_FAILURE,
  error,
})

export const fetchMoreInventoryItemTrackingForVariation = (
  variationId: string,
  from: number,
  to: number,
) => ({
  type: FETCH_MORE_INVENTORY_ITEM_TRACKING_FOR_VARIATION,
  variationId,
  from,
  to,
})
export const fetchMoreInventoryItemTrackingForVariationSuccess = (
  list: string[],
  from: number,
  totalCount: number,
) => ({
  type: FETCH_MORE_INVENTORY_ITEM_TRACKING_FOR_VARIATION_SUCCESS,
  list,
  from,
  totalCount,
})
export const fetchMoreInventoryItemTrackingForVariationFailure = (
  error: ApiError,
) => ({
  type: FETCH_MORE_INVENTORY_ITEM_TRACKING_FOR_VARIATION_FAILURE,
  error,
})

export const fetchMoreInventoryItemTrackingForVaccine = (
  vaccinationId: string,
  from: number,
  to: number,
) => ({
  type: FETCH_MORE_INVENTORY_ITEM_TRACKING_FOR_VACCINE,
  vaccinationId,
  from,
  to,
})
export const fetchMoreInventoryItemTrackingForVaccineSuccess = (
  list: string[],
  from: number,
  totalCount: number,
) => ({
  type: FETCH_MORE_INVENTORY_ITEM_TRACKING_FOR_VACCINE_SUCCESS,
  list,
  from,
  totalCount,
})
export const fetchMoreInventoryItemTrackingForVaccineFailure = (
  error: ApiError,
) => ({
  type: FETCH_MORE_INVENTORY_ITEM_TRACKING_FOR_VACCINE_FAILURE,
  error,
})

export const fetchMoreInventoryItemTrackingForVaccineWithVariation = (
  vaccinationId: string,
  from: number,
  to: number,
  includeExpiredItems: boolean,
) => ({
  type: FETCH_MORE_INVENTORY_ITEM_TRACKING_FOR_VACCINE_WITH_VARIATION,
  vaccinationId,
  from,
  to,
  includeExpiredItems,
})
export const fetchMoreInventoryItemTrackingForVaccineWithVariationSuccess = (
  list: string[],
  from: number,
  totalCount: number,
) => ({
  type: FETCH_MORE_INVENTORY_ITEM_TRACKING_FOR_VACCINE_WITH_VARIATION_SUCCESS,
  list,
  from,
  totalCount,
})
export const fetchMoreInventoryItemTrackingForVaccineWithVariationFailure = (
  error: ApiError,
) => ({
  type: FETCH_MORE_INVENTORY_ITEM_TRACKING_FOR_VACCINE_WITH_VARIATION_FAILURE,
  error,
})

export const updateInventoryItemTracking = (
  inventoryItemTracking: Record<string, InventoryItemTracking>,
) => ({
  type: UPDATE_INVENTORY_ITEM_TRACKING,
  inventoryItemTracking,
})

export const clearInventoryItemTracking = () => ({
  type: CLEAR_INVENTORY_ITEM_TRACKING,
})

export type InventoryItemTrackingState = {
  error: string | null
  isLoading: boolean
  list: string[]
  map: Record<string, InventoryItemTracking>
  totalCount: number
}

const INITIAL_STATE: InventoryItemTrackingState = {
  list: [],
  map: {},
  totalCount: Defaults.INFINITE_LIST_BATCH_LOAD_COUNT,
  isLoading: false,
  error: null,
}

export const inventoryItemTrackingReducer = (
  state: InventoryItemTrackingState = INITIAL_STATE,
  action: AnyAction,
): InventoryItemTrackingState => {
  switch (action.type) {
    case CLEAR_INVENTORY_ITEM_TRACKING:
      return {
        list: [],
        map: {},
        totalCount: Defaults.INFINITE_LIST_BATCH_LOAD_COUNT,
        isLoading: false,
        error: null,
      }
    case UPDATE_INVENTORY_ITEM_TRACKING:
      return {
        ...state,
        map: secondLevelMerge(state.map, action.inventoryItemTracking),
      }
    case FETCH_MORE_INVENTORY_ITEM_TRACKING_FOR_VACCINE:
    case FETCH_MORE_INVENTORY_ITEM_TRACKING_FOR_VACCINE_WITH_VARIATION:
      return {
        ...state,
        isLoading: true,
        error: null,
      }
    case FETCH_MORE_INVENTORY_ITEM_TRACKING_FOR_VACCINE_SUCCESS:
    case FETCH_MORE_INVENTORY_ITEM_TRACKING_FOR_VACCINE_WITH_VARIATION_SUCCESS:
      return {
        ...state,
        isLoading: false,
        list: R.uniq([...state.list, ...action.list]),
        totalCount: action.totalCount,
      }
    case FETCH_MORE_INVENTORY_ITEM_TRACKING_FOR_VACCINE_FAILURE:
    case FETCH_MORE_INVENTORY_ITEM_TRACKING_FOR_VACCINE_WITH_VARIATION_FAILURE:
      return {
        ...state,
        isLoading: false,
        error: getErrorMessage(action.error),
      }
    case FETCH_INVENTORY_ITEM_TRACKING_FOR_VARIATION:
      return {
        ...state,
        isLoading: true,
        error: null,
      }
    case FETCH_INVENTORY_ITEM_TRACKING_FOR_VARIATION_SUCCESS:
      return {
        ...state,
        isLoading: false,
        list: action.list,
        totalCount: action.totalCount,
      }
    case FETCH_INVENTORY_ITEM_TRACKING_FOR_VARIATION_FAILURE:
      return {
        ...state,
        isLoading: false,
        error: getErrorMessage(action.error),
      }
    case FETCH_MORE_INVENTORY_ITEM_TRACKING_FOR_VARIATION:
      return {
        ...state,
        isLoading: true,
        error: null,
      }
    case FETCH_MORE_INVENTORY_ITEM_TRACKING_FOR_VARIATION_SUCCESS:
      return {
        ...state,
        isLoading: false,
        list: R.uniq([...state.list, ...action.list]),
        totalCount: action.totalCount,
      }
    case FETCH_MORE_INVENTORY_ITEM_TRACKING_FOR_VARIATION_FAILURE:
      return {
        ...state,
        isLoading: false,
        error: getErrorMessage(action.error),
      }
    default:
      return state
  }
}

const getInventoryItemTracking = (
  state: RootState,
): InventoryItemTrackingState => state.inventoryItemTracking
export const getInventoryItemTrackingIsLoading = (state: RootState) =>
  getInventoryItemTracking(state).isLoading
export const getInventoryItemTrackingTotalCount = (state: RootState) =>
  getInventoryItemTracking(state).totalCount
export const getInventoryItemTrackingList = (state: RootState) =>
  getInventoryItemTracking(state).list
export const getInventoryItemTrackingMap = (state: RootState) =>
  getInventoryItemTracking(state).map
export const getInventoryItemTrackingItem = (id: string) =>
  createSelector(getInventoryItemTrackingMap, (map) => R.prop(id, map))
export const getMultipleInventoryItemTrackingItems = (ids: string[]) =>
  createSelector(getInventoryItemTrackingMap, (map) => R.props(ids, map))

export const getInventoryItemTrackingListSortedByExpirationDate =
  createSelector(
    [getInventoryItemTrackingList, getInventoryItemTrackingMap],
    (list, map) => {
      const items = list.map((id) => map[id]).filter(Boolean)
      const sortedItems = R.sortBy(
        (item) =>
          item.expirationDate ? moment(item.expirationDate).valueOf() : 0,
        items,
      )
      return sortedItems.map((item) => getInventoryItemTrackingKey(item))
    },
  )
